import React from 'react';
import type { AutoRefreshControl } from '@/hooks/useAutoRefresh';
import { InlineToggle } from '@/components/ui';

export type AutoRefreshIntervalOption = {
  value: number;
  label: string;
};

export type AutoRefreshToolbarProps = {
  control: AutoRefreshControl;
  intervalLabel?: string;
  intervalOptions?: AutoRefreshIntervalOption[];
  className?: string;
};

function formatLastUpdated(value: Date | null): string {
  if (!value) return '—';
  return value.toLocaleTimeString();
}

export default function AutoRefreshToolbar({
  control,
  intervalLabel,
  intervalOptions,
  className,
}: AutoRefreshToolbarProps) {
  const {
    enabled,
    setEnabled,
    refreshing,
    lastUpdatedAt,
    error,
    refresh,
    intervalMs,
    setIntervalMs,
  } = control;

  const resolvedIntervalLabel =
    intervalLabel ??
    intervalOptions?.find((option) => option.value === intervalMs)?.label ??
    `${Math.round(intervalMs / 1000)}s`;

  return (
    <div className={['auto-refresh-toolbar', className].filter(Boolean).join(' ')}>
      {refreshing ? (
        <span className="auto-refresh-toolbar__status" role="status" aria-live="polite">
          <span className="auto-refresh-toolbar__dot" aria-hidden />
          Refreshing…
        </span>
      ) : null}
      <span className="auto-refresh-toolbar__updated muted">
        Updated {formatLastUpdated(lastUpdatedAt)}
      </span>
      {intervalOptions && intervalOptions.length > 0 ? (
        <label className="auto-refresh-toolbar__interval">
          <span className="sr-only">Refresh interval</span>
          <select
            className="filter-select"
            value={intervalMs}
            onChange={(event) => setIntervalMs(Number(event.target.value))}
            aria-label="Refresh interval"
          >
            {intervalOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      ) : resolvedIntervalLabel ? (
        <span className="auto-refresh-toolbar__interval-label muted">{resolvedIntervalLabel}</span>
      ) : null}
      <InlineToggle
        checked={enabled}
        onChange={setEnabled}
        label="Auto-refresh"
        className="auto-refresh-toolbar__toggle"
      />
      <button
        type="button"
        className="btn btn-sm btn-ghost"
        onClick={() => void refresh()}
        disabled={refreshing}
      >
        Refresh
      </button>
      {error ? (
        <span className="auto-refresh-toolbar__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
